<template>
  <div class="data-table-wrapper">
    <div class="table-toolbar-expander">
      <div class="table-toolbar-expander-tab" @click="toolbarCollapsed = !toolbarCollapsed">
        {{ toolbarCollapsed ? '&#9650;' : '&#9660;' }}
      </div>
    </div>
    <draggable v-model="hiddenColumns" group="columns" class="col-hiding-container" ghost-class="ghost"
               v-if="draggingColumns">
      <icon :icon="['fal', 'eye-slash']" size="3x"/>
    </draggable>
    <div class="table-toolbar" v-if="!toolbarCollapsed">
      <div style="display: flex; flex-flow: row; gap: 5px">
        <div class="table-toolbar-action" @click="saveLayout">
          <icon :icon="['fal', 'save']"/>
          {{ $t("dataTable.saveLayout") }}
        </div>
        <div class="table-toolbar-action" @click="resetLayout">
          <icon :icon="['fal', 'undo']"/>
          {{ $t("dataTable.resetLayout") }}
        </div>
        <div class="table-toolbar-action" @click="handleExportExcel">
          <icon :icon="['fal', 'file-excel']"/>
          {{ $t("dataTable.exportExcel") }}
        </div>
        <div class="table-toolbar-action" @click="exportToCsv">
          <icon :icon="['fal', 'file-csv']"/>
          {{ $t("dataTable.exportCSV") }}
        </div>
      </div>
      <div>
        <b>{{ $t("dataTable.newConditionalRule") }}:</b>
        <form-wrapper>
          <form-select-input :caption="$t('dataTable.conditional.column')" :options="conditionalColumnOptions"
                             v-model="newConditional.column"/>
          <form-select-input :caption="$t('dataTable.conditional.operation')"
                             :options="conditionalOperationOptions"
                             v-model="newConditional.operation"/>
          <form-text-input :caption="$t('dataTable.conditional.value')" v-model="newConditional.value"/>
          <!--          <form-select-input :caption="$t('dataTable.conditional.target')" :options="conditionalTargetOptions" v-model="newConditional.target"/>-->
          <form-color-input :caption="$t('dataTable.conditional.bgColor')" v-model="newConditional.bgColor"/>
          <form-color-input :caption="$t('dataTable.conditional.fgColor')" v-model="newConditional.fgColor"/>
          <form-checkbox-input :caption="$t('dataTable.conditional.bold')" v-model="newConditional.bold"/>
          <form-checkbox-input :caption="$t('dataTable.conditional.italic')" v-model="newConditional.italic"/>
          <form-checkbox-input :caption="$t('dataTable.conditional.underline')"
                               v-model="newConditional.underline"/>
        </form-wrapper>
        <simple-button :disabled="!validNewConditional" @press="saveToConditionals">
          {{ $t('dataTable.conditional.addConditional') }}
        </simple-button>
      </div>
      <div>
        <h1>Pravidla:</h1>
        <draggable :list="conditionals" :group="{ name: 'conditionals' }" class="drop-area">

          <div class="conditional-box" v-for="conditional in conditionals">
            <div style="gap: 5px;">
              <simple-button @press="removeConditional(conditional)">
                Odebrat
              </simple-button>
              <simple-button @press="editConditional(conditional)">
                Upravit
              </simple-button>
              <simple-button @press="duplicateConditional(conditional)">
                Duplikovat
              </simple-button>
            </div>

            <div class="conditional-style" :style="{
                  'background-color': conditional.bgColor != null ? conditional.bgColor : 'var(--card-background-color)',
                  'color': conditional.fgColor,
                  'text-decoration': conditional.underline ? 'underline' : undefined,
                  'font-weight': conditional.bold ? 'bold' : undefined,
                  'font-style': conditional.italic ? 'italic' : undefined
                 }">{{ conditional.column }} {{ conditional.operation }} {{ conditional.value }}
            </div>

          </div>
        </draggable>
        <!--        {{ conditionals }}-->
      </div>
    </div>

    <table class="data-table">
      <thead class="data-table-thead">
      <tr v-if="typeof internalConfiguration.tableCaption != 'undefined'">
        <th class="data-table-th data-table-caption" :colspan="internalConfiguration.columns.length">
          {{ internalConfiguration.tableCaption }}
        </th>
      </tr>
      <tr>
        <th class="data-table-th" v-if="internalConfiguration.enableCheck">
          <icon :icon="['fal', 'check-double']" @click="toggleAll"/>
        </th>
        <draggable v-model="internalConfiguration.columns" tag="div" style="display: contents;" group="columns"
                   @start="draggingColumns = true" @end="draggingColumns = false">
          <th class="data-table-th" v-for="(column, columnIndex) in internalConfiguration.columns"
              :key="`${columnIndex}column.name`"
              @click="toggleSort(column)">
            <div>
              {{ column.caption }}
              <template v-if="currentSort.column == column">
                <icon :icon="['fal', currentSort.direction === 'ASC' ? 'sort-up' : 'sort-down']"/>
              </template>
            </div>
          </th>
        </draggable>
        <th class="data-table-th" v-if="Array.isArray(internalConfiguration.actions)">{{
            $t("dataTable.actions")
          }}
        </th>

      </tr>
      </thead>
      <tbody>
      <template v-for="(entry, rowIdx) in data">
        <tr :class="{'data-table-tr': true, 'selection': focusedEntry == entry }">
          <td class="data-table-td data-table-td-action"
              v-if="internalConfiguration.enableCheck">
            <template
              v-if="(typeof internalConfiguration.checkLambda === 'function' && internalConfiguration.checkLambda(entry)) || (!internalConfiguration.checkLambda)">
              <input type="checkbox" :checked="selectedEntries.includes(entry)"
                     @change="toggleSelection(entry, $event)"/>
            </template>
          </td>
          <td class="data-table-td"
              :style="typeof column.style != 'undefined' ? getStyleForCell(column, entry, rowIdx, colIdx) : null"
              @click="toggleFocus(rowIdx, colIdx, entry, column.datum(entry, rowIdx, colIdx))"
              v-for="(column, colIdx) in internalConfiguration.columns">
            {{
              column.datumFormat ?
                column.datumFormat(column.datum(entry, rowIdx, colIdx)) : column.datum(entry, rowIdx, colIdx)
            }}
          </td>
          <td class="data-table-td" style="padding: 0 0;" v-if="Array.isArray(internalConfiguration.actions)">
            <div class="action-container">
              <div v-for="action in internalConfiguration.actions" class="action-item"
                   v-if="typeof action.accessLambda === 'undefined' || action.accessLambda(entry)"
                   @click="emitAction(action, entry)"
                   v-tooltip="typeof action.tooltip === 'function' ? action.tooltip(entry) : action.tooltip"
              >
                <template
                  v-if="loadingActions.find(ac => ac.action == action && ac.entry == entry) == undefined">
                  <icon :icon="['fal', action.icon]" class="action-icon"/>
                </template>
                <template v-else>
                  <icon :icon="['fal', 'spinner']" class="action-icon" spin/>
                </template>
              </div>
            </div>
          </td>
        </tr>
        <tr v-if="internalConfiguration.allowEdit && focusedEntry == entry">
          <td :colspan="totalCols" class="editation-cell" v-if="$slots.default">
            <slot/>
          </td>
        </tr>
      </template>

      <template v-if="internalConfiguration.enableAdd && internalConfiguration.allowEdit && focusedEntry == null">
        <tr>
          <td class="editation-cell" :colspan="totalCols">
            <simple-button @press="handleAdd">
              {{ $t("dataTable.create", {objectName: internalConfiguration.objectName}) }}
            </simple-button>
          </td>
        </tr>
        <tr>
          <td :colspan="totalCols" class="editation-cell">
            <slot/>
          </td>
        </tr>
      </template>


      <tr>
        <td v-if="pagination" class="data-table-pagination" :colspan="totalCols">
          <paginator :pagination="pagination" @pageChanged="handlePageChanged"/>
        </td>
      </tr>
      <tr>
        <td class="data-table-td td-add"
            :colspan="internalConfiguration.columns.length + (internalConfiguration.enableDelete ? 1 : 0)"
            v-if="internalConfiguration.enableAdd"
            @click="handleAdd">
        </td>
      </tr>
      </tbody>
    </table>
    <div class="selection-info" v-if="internalConfiguration.enableCheck">
      {{ this.$t("dataTable.selected", {selected: selectedEntries.length}) }}
    </div>
  </div>
</template>

<script>
import Paginator from "../Paginator";
import SimpleButton from "../button/SimpleButton";
import handleTableExport from '@/utils/excel-utils'
import FormTextInput from "@/components/form/FormTextInput.vue";
import FormSelectInput from "@/components/form/FormSelectInput.vue";
import FormWrapper from "@/components/form/FormWrapper.vue";
import FormColorInput from "@/components/form/FormColorInput.vue";
import FormCheckboxInput from "@/components/form/FormCheckboxInput.vue";
import { mapGetters } from "vuex";

const assignDefined = (target, ...sources) => {
  for (const source of sources) {
    for (const key of Object.keys(source)) {
      const val = source[key];
      if (val !== undefined && val !== null) {
        target[key] = val;
      }
    }
  }
  return target;
}

const createEmptyConditional = () => ({
  column: null,
  operation: null,
  value: '',
  target: null,
  bgColor: null,
  fgColor: null,
  underline: null
});

export default {
  name: "DataTable",
  components: {
    FormCheckboxInput,
    FormColorInput,
    FormWrapper,
    FormSelectInput,
    FormTextInput,
    SimpleButton,
    Paginator
  },
  props: {
    configuration: Object,
    data: Array,
    pagination: {
      type: Object
    },
    defaultSort: Object,
    forgetFocused: Boolean,
    componentId: String,
    enableConditional: Boolean
  },
  data: function () {
    return {
      newConditional: createEmptyConditional(),
      conditionals: [],
      loadingActions: [],
      allSelected: false,
      selectedEntries: [],
      focusedEntry: null,
      currentSort: {
        column: this.internalConfiguration?.columns.find(col => col.sortField == this.defaultSort?.sortField) ?? {},
        ...this.defaultSort ?? {column: {}, sortField: "", direction: ""}
      },
      internalConfiguration: Object,
      hiddenColumns: [],
      toolbarCollapsed: true,
      draggingColumns: false
    }
  },
  async mounted() {
    await this.$store.dispatch("personalization/FETCH_BY_USER", {userId: this.loggedUser?.id})
  },
  watch: {
    data: {
      handler() {
        if (typeof this.data == 'undefined' || this.data == null || !this.data.includes(this.focusedEntry)) {
          this.focusedEntry = null;
        }
      }
    },
    configuration: {
      handler(newVal) {
        this.internalConfiguration = this.$_.cloneDeep(newVal);
        if (this.personalizedLayout) {
          const colByName = col => this.internalConfiguration.columns.find(iCol => iCol.caption == col.name);
          this.internalConfiguration.columns = this.personalizedLayout.columns.map(colByName);
          this.conditionals = this.$_.cloneDeep(this.personalizedLayout.conditionals) ?? [];

          const sortingColumn = this.personalizedLayout.columns.find(col => col.sortOrder);

          if (sortingColumn?.name) {
            const internalColumn = this.internalConfiguration.columns.find(col => col.caption === sortingColumn.name);
            this.currentSort.column = internalColumn;
            this.currentSort.direction = sortingColumn.sortOrder;
            this.currentSort.sortField = internalColumn.sortField;
            this.$emit('sortChanged', {sortField: this.currentSort.sortField, direction: this.currentSort.direction});
          }

        }
      },
      immediate: true
    }
  },
  computed: {

    ...mapGetters({
      actualPrincipal: 'auth/actualPrincipal',
      users: "users/users",
    }),

    loggedUser() {
      return this.users?.find(user => user.username === this.actualPrincipal.username)
    },

    validNewConditional() {
      return this.newConditional.column &&
        this.newConditional.value &&
        this.newConditional.operation;
    },
    conditionalTargetOptions() {
      return [
        {name: this.$t('dataTable.conditional.targetRow'), id: 'row'},
        {name: this.$t('dataTable.conditional.targetColumn'), id: 'column'},
      ]
    },
    conditionalOperationOptions() {
      return [
        {name: this.$t('dataTable.conditional.gt'), id: "gt"},
        {name: this.$t('dataTable.conditional.lt'), id: "lt"},
        {name: this.$t('dataTable.conditional.eq'), id: "eq"},
        {name: this.$t('dataTable.conditional.like'), id: "like"},
        {name: this.$t('dataTable.conditional.in'), id: "in"}
      ]
    },
    conditionalColumnOptions() {
      return this.internalConfiguration.columns.map(col => ({name: col.caption, id: col.caption}));
    },
    totalCols() {
      return this.internalConfiguration.columns.length + (this.internalConfiguration.enableCheck ? 1 : 0) +
        (typeof this.internalConfiguration.actions != 'undefined' ? this.internalConfiguration.actions.length : 0)
    },
    showLayoutOptions() {
      return true;
    },
    personalizedLayout() {
      return this.$store.getters["personalization/GET_PERSONALIZATION_LIST"](this.componentId);
    }
  },
  methods: {
    getStyleForCell(column, entry, rowIdx, colIdx) {
      const style = column?.style(entry, rowIdx, colIdx) ?? {};
      const finalStyle = {...style, ...this.getConditionalStyle(column, column.datum(entry, rowIdx, colIdx))};
      return finalStyle;
    },
    evaluateConditional(operation, conditionalValue, columnValue) {
      const parseNum = (val) => Number.parseFloat(val.replace(",", ".").replace(/\s+/g, ""));
      // console.log(operation, conditionalValue, columnValue)
      if (operation == "lt") {
        // console.log(parseNum(conditionalValue), parseNum(columnValue))
        return parseNum(conditionalValue) > parseNum(columnValue);
      } else if (operation == "gt") {
        return parseNum(conditionalValue) < parseNum(columnValue);
      } else if (operation == "eq") {
        if (Number.isNaN(Number.parseFloat(conditionalValue))) {
          return conditionalValue === columnValue;
        } else {
          return parseNum(conditionalValue) == parseNum(columnValue);
        }
      } else if (operation == "like") {
        return columnValue.normalize('NFKD').replace(/[^\w]/g, '').toLowerCase().includes(conditionalValue.normalize('NFKD').replace(/[^\w]/g, '').toLowerCase())
      } else if (operation == "in") {
        const vals = conditionalValue.split(";")
        return !!vals.find(val => val.toLowerCase().equals(columnValue.toLowerCase()))
      }
      return false;
    },
    getConditionalStyle(column, value) {
      const conditionalFormatting = this.conditionals
        .filter(cond => cond.column == column.caption)
        .filter(cond => this.evaluateConditional(cond.operation, cond.value, value))
      if (conditionalFormatting.length > 0) {
        const conditionalStyles = conditionalFormatting.map(condForm => {
          return {
            'background-color': condForm.bgColor,
            'color': condForm.fgColor,
            'font-weight': condForm.bold ? 'bold' : undefined,
            'font-style': condForm.italic ? 'italic' : undefined,
            'text-decoration': condForm.underline ? 'underline' : undefined,
          }
        });
        return assignDefined({}, ...conditionalStyles);
      }
      return null;
    },
    saveToConditionals() {
      if (!this.conditionals.includes(this.newConditional)) {
        this.conditionals.push(this.newConditional);
      }
      this.newConditional = createEmptyConditional();
    },
    handleExportExcel() {
      handleTableExport(this.internalConfiguration.columns, this.data, "table");
    },
    resetLayout() {
      this.internalConfiguration.columns = this.$_.cloneDeep(this.configuration.columns);
      this.conditionals = this.$_.cloneDeep(this.personalizedLayout.conditionals) ?? [];
    },
    async saveLayout() {
      const columns = this.internalConfiguration.columns.map(col => ({
        name: col.caption
      }))
      if (this.currentSort.sortField) {
        const sortedCol = columns.find(col => col.name === this.currentSort.column.caption)
        sortedCol.sortOrder = this.currentSort.direction;
      }
      const personalizationConfig = {
        ...this.personalizedLayout,
        columns: columns,
        listId: this.componentId,
        name: "DEFAULT",
        conditionals: this.conditionals,
        owner: this.loggedUser.id
      };
      try {
        await Promise.all([
            this.$store.dispatch("personalization/SAVE_PERSONALIZATION_LIST", personalizationConfig)
          ]
        )
        this.$notify({
          group: 'application',
          type: 'ok',
          title: 'Layout saved',
          text: 'Table layout was stored under your personalized settings.'
        })
      } catch (e) {
        console.warn('Chyba pri ukladani nastaveni')
        console.log(e)
      }

    },
    toggleSort(column) {
      this.allSelected = false;
      this.clearSelection();

      if (!column.allowSort) return;

      if (this.currentSort.sortField == column.sortField) {
        if (this.currentSort.direction === "ASC")
          this.currentSort.direction = "DESC";
        else {
          this.currentSort.column = {};
          this.currentSort.sortField = null;
          this.currentSort.direction = null;
        }
      } else {
        this.currentSort.column = column;
        this.currentSort.direction = "ASC";
        this.currentSort.sortField = column.sortField;
      }
      this.$emit('sortChanged', {sortField: this.currentSort.sortField, direction: this.currentSort.direction});
    },
    async emitAction(action, entry) {
      const handlerName = action.handlerName ?? action.name;
      if (action.isAsync) {
        let loadingEntry = {action, entry};
        this.loadingActions.push(loadingEntry);
        await this.$listeners[handlerName](entry);
        this.loadingActions.splice(this.loadingActions.indexOf(loadingEntry), 1);
      } else {
        this.$emit(handlerName, entry, action);
      }
    },
    handlePageChanged(page, pageSize) {
      this.allSelected = false;
      this.clearSelection();
      this.$emit('pageChanged', page, pageSize);
    },
    handleAdd() {
      this.focusedEntry = null;
      this.$emit('add');
    },
    handleDelete(entry) {
      this.$emit('delete', entry);
    },
    clearSelection() {
      this.selectedEntries.splice(0, this.selectedEntries.length)
    },
    toggleAll() {
      if (this.selectedEntries.length > 0) {
        this.allSelected = false;
        this.clearSelection();
      } else {
        this.allSelected = true;
        this.clearSelection();
        this.selectedEntries = [...this.data];
      }
      this.$emit('selectionChanged', this.selectedEntries);
    },
    toggleSelection(entry) {
      if (!this.selectedEntries.includes(entry))
        this.selectedEntries.push(entry)
      else
        this.selectedEntries.splice(this.selectedEntries.indexOf(entry), 1)
      this.$emit('selectionChanged', this.selectedEntries)
    },
    exportToCsv() {
      let csvFile = this.internalConfiguration.columns.map(col => col.caption).join(";") + "\r\n";
      csvFile += this.data
        .map(entry => this.internalConfiguration.columns
          .map(col => col.datum(entry)).join(";")
        ).join("\r\n")

      let blob = new Blob([csvFile], {type: 'text/csv;charset=utf-8;'});
      if (navigator.msSaveBlob) { // IE 10+
        navigator.msSaveBlob(blob, 'table-export.csv');
      } else {
        let link = document.createElement("a");
        if (link.download !== undefined) { // feature detection
          // Browsers that support HTML5 download attribute
          let url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", 'table-export.csv');
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    },
    duplicateConditional(conditional) {
      this.newConditional = {...conditional};
    },
    editConditional(conditional) {
      this.newConditional = conditional;
    },
    removeConditional(conditional) {
      const condIdx = this.conditionals.indexOf(conditional);
      this.conditionals.splice(condIdx, 1);
    },
    toggleFocus(rowIdx, colIdx, entry, datum) {
      if (!this.forgetFocused) {
        if (typeof this.internalConfiguration.focusChangedCallback !== 'undefined') {
          const result = this.internalConfiguration.focusChangedCallback(rowIdx, colIdx, entry, datum);
          if (typeof result !== 'undefined' && result == false) {
            return false;
          }
        }

        if (this.focusedEntry == entry) this.focusedEntry = null;
        else this.focusedEntry = entry;
        this.$emit("focusedChanged", this.focusedEntry == null ? null : this.$_.cloneDeep(this.focusedEntry));
      } else {
        this.$emit("focusedChanged", entry);
      }

    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/global";

.data-table-wrapper {
  overflow-x: auto;
  position: relative;
  break-inside: avoid;
}

tbody > .data-table-tr:hover {
  background-color: var(--table-body-highlight-color);
  color: var(--table-body-font-highlight-color);
  transition: 0.15s ease;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  border-style: hidden;
  background-color: var(--table-body-color);
}

.data-table-thead > tr > th {
  position: sticky;
  top: -20px;
  z-index: 1;
}

.data-table-thead > tr > td {
  top: 0;
  z-index: 1;
}

.data-table-caption {
  text-align: left;
}

.data-table-th {
  background-color: var(--table-header-color);
  color: var(--table-header-font-color);
  border-bottom: 1px solid var(--table-border-color);
  border-right: 1px solid var(--table-border-color);
  font-weight: 600;
  padding: 10px 8px 10px 8px;
  text-align: left;
  white-space: nowrap;
  user-select: none;
  cursor: grab;
}


.data-table-td {
  cursor: default;
  border-bottom: 1px solid var(--table-border-color);
  border-right: 1px solid var(--table-border-color);
  padding: 4px 8px 4px 8px;
  white-space: nowrap;
}

.data-table-td-action {
  cursor: pointer;
  text-align: center;
  padding: 2px 4px 2px 4px;
  width: 20px;
  user-select: none;
}

.selection {
  background-color: var(--table-body-selection-color);
  color: var(--table-body-font-selection-color);
  transition: 0.2s;
}

.selection > td {
  /*border-bottom: 1px solid var(--accent-color);*/
  /*border-top: 1px solid var(--accent-color);*/

}

.td-add {
  color: lighten($navigationGray, 50);
  cursor: pointer;
  //background-color: $openRed;
}

.selection-info {
  padding: 5px;
  color: lighten($navigationGray, 50)
}

.data-table-pagination {
  padding: 5px;
}

.small-icon {
  display: inline-block;
  background-color: $openRed;
  color: white;
  padding: 2px 6px 2px 6px;
  border-radius: 5px;
  margin: 4px;
  text-align: center;
}

.editation-cell {
  padding: 5px 5px 5px 5px;
  background-color: var(--table-border-color)
}

.action-icon {

}

.action-container {
  display: flex;
  padding: 3px 3px 3px 3px;
  height: 100%;
  width: 100%;
  background-color: var(--card-background-color);
}

.action-item {
  margin-left: 2px;
  height: 100%;
  padding: 5px 5px 5px 5px;
  background: linear-gradient(to bottom, var(--accent-color) 5%, #C85858 100%);
  color: white;
  border-radius: 2px;
}

.action-item:hover {
  cursor: pointer;
  /*border: 1px solid var(--accent-color-plus-50);*/
  background: linear-gradient(to bottom, #ffaf34 5%, #ffe98e 100%);
  color: var(--background-color);
}

.table-toolbar {
  display: flex;
  flex-flow: column;
  grid-gap: 5px;
  background-color: var(--table-border-color);
  padding: 5px;
}

.table-toolbar-expander {
  width: 100%;
  display: flex;
  justify-content: center;
  border-bottom: 1px solid var(--table-border-color);
}

.table-toolbar-expander-tab {
  height: 11px;
  font-size: 9px;
  width: 40px;
  text-align: center;
  background-color: var(--table-border-color);
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  cursor: pointer;
  user-select: none;
}

.table-toolbar-action {
  border-radius: 2px;
  padding: 5px;
  background-color: darken(#586674, 10);
  cursor: pointer;
  user-select: none;
}

.col-hiding-container {
  display: flex;
  position: absolute;

  justify-content: center;
  align-items: center;
  padding: 10px;
  border-radius: 50px;
  border: 2px dashed lighten(#586674, 20);
  margin-left: calc(50% - 50px);
  margin-top: 40px;
  width: 100px;
  height: 100px;
  background-color: var(--table-border-color);
}

.ghost {
  display: none;
}

.conditional-box {
  & * {
    display: flex;
    padding: 4px;
    justify-content: center;
    align-items: center
  }

  display: flex;
  flex-flow: row;
  padding: 2px;
  margin: 2px;
  background-color: var(--table-header-font-color);
  border-radius: 5px;
}

.conditional-style {
  width: 100%;
}
</style>
